import {
  Badge,
  Box,
  Button,
  Code,
  Flex,
  Heading,
  HStack,
  Spinner,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { ArrowDownIcon, ClipboardIcon, DownloadIcon, RefreshCwIcon, XIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { k8sApi, type LogFileContent } from '~/lib/api'
import { toaster } from '~/toaster'

interface LogViewerModalProps {
  podName: string
  filePath: string
  onClose: () => void
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export function LogViewerModal({ podName, filePath, onClose }: LogViewerModalProps) {
  const [tailMode, setTailMode] = useState(false)
  const [autoScroll, setAutoScroll] = useState(true)
  const logContainerRef = useRef<HTMLDivElement>(null)
  const fileName = filePath.split('/').pop() || filePath

  // Fetch log content
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['hivemq-log-content', podName, filePath],
    queryFn: () => k8sApi.hivemqLogContent(podName, filePath),
    refetchInterval: tailMode ? 5000 : false, // Auto-refresh every 5s in tail mode
  })

  // Auto-scroll to bottom when content changes
  useEffect(() => {
    if (autoScroll && logContainerRef.current && data) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight
    }
  }, [data, autoScroll])

  const handleCopyToClipboard = async () => {
    if (data?.content) {
      try {
        await navigator.clipboard.writeText(data.content)
        toaster.create({
          title: 'Copied to clipboard',
          type: 'success',
        })
      } catch {
        toaster.create({
          title: 'Failed to copy',
          type: 'error',
        })
      }
    }
  }

  const handleDownload = async () => {
    try {
      const blob = await k8sApi.hivemqLogDownload(podName, filePath)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toaster.create({
        title: 'Download started',
        description: `Downloading ${fileName}`,
        type: 'success',
      })
    } catch (error) {
      toaster.create({
        title: 'Download failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        type: 'error',
      })
    }
  }

  const scrollToBottom = () => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight
    }
  }

  return (
    <Box position="fixed" inset={0} bg="blackAlpha.600" zIndex={1000} onClick={onClose}>
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        bg="bg.panel"
        borderRadius="lg"
        w="95vw"
        maxW="1400px"
        h="90vh"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
        display="flex"
        flexDirection="column"
      >
        {/* Header */}
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderBottomWidth="1px"
          borderColor="border"
          flexShrink={0}
        >
          <VStack align="flex-start" gap={1}>
            <Heading size="md">{fileName}</Heading>
            <HStack gap={3}>
              <Text fontSize="sm" color="content.secondary">
                Pod: {podName}
              </Text>
              {data && (
                <>
                  <Badge variant="outline" fontSize="xs">
                    {formatBytes(data.fileSize)}
                  </Badge>
                  <Badge variant="outline" fontSize="xs">
                    {data.lineCount} lines
                  </Badge>
                  {data.truncated && (
                    <Badge colorPalette="yellow" variant="subtle" fontSize="xs">
                      Showing last 1000 lines
                    </Badge>
                  )}
                </>
              )}
            </HStack>
          </VStack>
          <HStack gap={3}>
            {/* Tail Mode Toggle */}
            <HStack gap={2}>
              <Text fontSize="sm" color="content.secondary">
                Tail Mode
              </Text>
              <Switch.Root
                checked={tailMode}
                onCheckedChange={(e) => setTailMode(e.checked)}
                size="sm"
              >
                <Switch.HiddenInput aria-label="Toggle tail mode" />
                <Switch.Control>
                  <Switch.Thumb />
                </Switch.Control>
              </Switch.Root>
            </HStack>

            {/* Auto-scroll Toggle */}
            <HStack gap={2}>
              <Text fontSize="sm" color="content.secondary">
                Auto-scroll
              </Text>
              <Switch.Root
                checked={autoScroll}
                onCheckedChange={(e) => setAutoScroll(e.checked)}
                size="sm"
              >
                <Switch.HiddenInput aria-label="Toggle auto-scroll" />
                <Switch.Control>
                  <Switch.Thumb />
                </Switch.Control>
              </Switch.Root>
            </HStack>

            <Button size="sm" variant="outline" onClick={scrollToBottom}>
              <ArrowDownIcon size={16} />
              Bottom
            </Button>
            <Button size="sm" variant="outline" onClick={handleCopyToClipboard}>
              <ClipboardIcon size={16} />
              Copy
            </Button>
            <Button size="sm" variant="outline" onClick={handleDownload}>
              <DownloadIcon size={16} />
              Download
            </Button>
            <Button size="sm" variant="outline" onClick={() => refetch()} loading={isFetching}>
              <RefreshCwIcon size={16} />
              Refresh
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onClose}
              aria-label="Close log viewer"
              minW="44px"
              minH="44px"
            >
              <XIcon size={16} />
            </Button>
          </HStack>
        </Flex>

        {/* Log Content */}
        <Box ref={logContainerRef} flex={1} overflow="auto" bg="gray.900" p={4}>
          {isLoading ? (
            <Flex h="100%" align="center" justify="center">
              <Spinner color="white" size="lg" />
            </Flex>
          ) : data?.content ? (
            <Code
              display="block"
              whiteSpace="pre"
              wordBreak="break-all"
              fontSize="xs"
              fontFamily="mono"
              color="green.300"
              bg="transparent"
              p={0}
              lineHeight="1.6"
            >
              {data.content}
            </Code>
          ) : (
            <Flex h="100%" align="center" justify="center">
              <Text color="gray.500">No content available</Text>
            </Flex>
          )}
        </Box>

        {/* Status Bar */}
        {tailMode && (
          <Flex
            px={4}
            py={2}
            borderTopWidth="1px"
            borderColor="border"
            bg="info.subtle"
            align="center"
            gap={2}
            flexShrink={0}
          >
            <Spinner size="xs" />
            <Text fontSize="xs" color="info.fg">
              Tail mode active - refreshing every 5 seconds
            </Text>
            {data?.timestamp && (
              <Text fontSize="xs" color="content.secondary" ml="auto">
                Last updated: {new Date(data.timestamp).toLocaleTimeString()}
              </Text>
            )}
          </Flex>
        )}
      </Box>
    </Box>
  )
}
